import React, { Component } from 'react';
import { ReactPixel, track } from '../../helpers';
import { CONTEXT_SEARCH } from '../../helpers/trackingStringValues';
import { search } from '../../helpers/routes';
import TrackingEvents from '../../common/models/TrackingEvents';
import Departments from '../../common/models/Departments';
import DepartmentBox from '../DepartmentBox/DepartmentBox';
import SearchDepartment from './SearchDepartment';
import { SearchBoxIcon, SearchInput, SearchWrapper } from './styled';
import withCountryCode, { BuildUrlWithCountry } from '../RoutesHoc/RoutesHoc';

/* Types */
interface SearchBoxProps {
  buildUrlWithCountry: BuildUrlWithCountry;
  department: string;
  departmentUpdated: (department: string) => void;
  hideSearchBox?: () => void;
  mobileSubmit?: boolean;
  queryUpdated: (value: string) => void;
  s3BasePath: string;
  searchQuery: string;
  setDepartmentVisibility: (value: boolean) => void;
  showDepartments?: boolean;
}

/* Components */
class SearchBox extends Component<SearchBoxProps> {
  private searchInputRef = React.createRef<HTMLDivElement>();

  private searchBoxRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);
    this.handleSearchInput = this.handleSearchInput.bind(this);
    this.submitSearch = this.submitSearch.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handlerDepartment = this.handlerDepartment.bind(this);
    this.handleFocusOutIfMobile = this.handleFocusOutIfMobile.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    this.searchBoxRef.current.addEventListener('focusout', this.handleFocusOutIfMobile);
  }

  componentDidUpdate() {
    const { showDepartments } = this.props;
    if (!showDepartments) {
      this.searchInputRef.current.blur();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    this.searchBoxRef.current.removeEventListener('focusout', this.handleFocusOutIfMobile);
  }

  handleFocusOutIfMobile(e) {
    const { mobileSubmit } = this.props;
    const { relatedTarget } = e;
    const { searchBoxRef } = this;

    if (!mobileSubmit || !searchBoxRef) {
      return null;
    }

    const { current: searchBox } = searchBoxRef;
    if (!relatedTarget || searchBox === relatedTarget || !searchBox.contains(relatedTarget)) {
      this.submitSearch();
      return null;
    }

    return null;
  }

  handleClickOutside(event) {
    const { setDepartmentVisibility } = this.props;
    if (this.searchBoxRef && this.searchBoxRef.current && !this.searchBoxRef.current.contains(event.target)) {
      setDepartmentVisibility(false);
    }
  }

  handleSearchInput(e: React.ChangeEvent<HTMLInputElement>) {
    const { queryUpdated } = this.props;
    queryUpdated(e.target.value);
  }

  submitSearch(e?: React.FormEvent) {
    if (e) {
      e.preventDefault();
    }

    const { department, searchQuery, buildUrlWithCountry } = this.props;
    const searchDepartment = Departments.getInternalName(department);
    const term = searchQuery.trim();
    if (!term) {
      return;
    }

    /* eslint-disable @typescript-eslint/camelcase */
    ReactPixel.track('Search', { search_string: searchQuery });
    track(TrackingEvents.productsSearched(), {
      categoryDepartment: { department: searchDepartment.toLowerCase() },
      ui: { term },
    });
    window.location = (buildUrlWithCountry(search(searchDepartment, term)) as undefined) as Location;
  }

  handlerDepartment(department, e) {
    e.stopPropagation();

    const { departmentUpdated } = this.props;
    departmentUpdated(department);
    const searchDepartment = Departments.getInternalName(department);
    track(TrackingEvents.departmentSelected(), {
      categoryDepartment: {
        department: searchDepartment.toLowerCase(),
      },
      ui: { context: CONTEXT_SEARCH },
    });
    this.searchInputRef.current.focus();
  }

  render() {
    const {
      department,
      setDepartmentVisibility,
      s3BasePath,
      showDepartments,
      searchQuery,
      hideSearchBox,
      mobileSubmit,
    } = this.props;

    return (
      <SearchWrapper
        ref={this.searchBoxRef}
        onClick={() => {
          if (mobileSubmit) {
            hideSearchBox();
          }
        }}
        style={{ display: 'flex' }}
        tabIndex="0"
      >
        <form id="search" onSubmit={this.submitSearch} tabIndex={0}>
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative' }} tabIndex={0}>
            <SearchInput
              ref={this.searchInputRef}
              autoComplete="off"
              data-cy="input-search-free-text"
              onChange={this.handleSearchInput}
              onClick={e => e.stopPropagation()}
              onFocus={() => setDepartmentVisibility(true)}
              tabIndex="0"
              type="search"
              value={searchQuery}
            />

            <SearchDepartment showDepartments={showDepartments} tabIndex="0">
              <DepartmentBox
                context="search"
                department={department}
                fontSize="13px"
                handleClick={this.handlerDepartment}
                tabIndex="0"
                underline
              />
            </SearchDepartment>

            <SearchBoxIcon
              alt="Submit search"
              data-cy="image-search-submit"
              id="Search"
              onClick={e => {
                e.stopPropagation();
                this.submitSearch(e);
              }}
              src={`${s3BasePath}mkt/header/magnifying_glass.svg`}
              tabIndex="0"
              type="image"
            />
          </div>
        </form>
      </SearchWrapper>
    );
  }
}

export default withCountryCode(SearchBox);
